/**
 * Modal de "Resetar senha" — TH-53165.
 *
 * POST /users/:id/reset-password retorna `{ reset_url }`. Mostramos
 * o link pra que o admin copie e mande pro usuário (até M1.E5
 * EmailSender entregar).
 */

import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userEmail: string;
}

async function requestReset(userId: string): Promise<string> {
  const res = await fetch(`/api/users/${userId}/reset-password`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  const body = (await res.json()) as { reset_url: string };
  return body.reset_url;
}

export function ResetPasswordDialog({
  open,
  onOpenChange,
  userId,
  userEmail,
}: Props) {
  const [resetUrl, setResetUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => requestReset(userId),
    onSuccess: (url) => {
      setResetUrl(url);
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  useEffect(() => {
    if (!open) {
      setResetUrl(null);
      setError(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Resetar senha</DialogTitle>
        <DialogDescription>
          Gera um link de redefinição de senha para <strong>{userEmail}</strong>.
          O link expira em 30 minutos. Quando o EmailSender (M1.E5) entrar em
          produção, este link irá direto pro email do usuário.
        </DialogDescription>

        {error ? (
          <div
            role="alert"
            className="mt-3 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
          >
            {error}
          </div>
        ) : null}

        {resetUrl ? (
          <div className="mt-4 space-y-3 rounded-md border border-[var(--color-border)] bg-[var(--color-secondary)] p-4">
            <p className="text-xs text-[var(--color-muted-foreground)]">
              Link de redefinição
            </p>
            <p className="select-all break-all font-mono text-xs">
              {resetUrl}
            </p>
          </div>
        ) : null}

        <div className="mt-6 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {resetUrl ? "Fechar" : "Cancelar"}
          </Button>
          {!resetUrl ? (
            <Button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Gerando…" : "Gerar link"}
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => navigator.clipboard.writeText(resetUrl)}
            >
              Copiar link
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
